import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth, landingForRole } from "@/context/AuthContext";
import { I18nProvider } from "@/lib/i18n";
import ProtectedRoute from "@/components/ProtectedRoute";
import LoginPage from "@/pages/LoginPage";
import KioskPage from "@/pages/KioskPage";
import DisplayPage from "@/pages/DisplayPage";
import CounterPage from "@/pages/CounterPage";
import AccessDeniedPage from "@/pages/AccessDeniedPage";
import TicketStatusPage from "@/pages/TicketStatusPage";
import AdminLayout from "@/pages/admin/AdminLayout";
import DashboardPage from "@/pages/admin/DashboardPage";
import AgenciesPage from "@/pages/admin/AgenciesPage";
import CountersPage from "@/pages/admin/CountersPage";
import CategoriesPage from "@/pages/admin/CategoriesPage";
import UsersPage from "@/pages/admin/UsersPage";
import HistoryPage from "@/pages/admin/HistoryPage";
import AuditPage from "@/pages/admin/AuditPage";
import ErrorLogsPage from "@/pages/admin/ErrorLogsPage";
import SettingsPage from "@/pages/admin/SettingsPage";
import { Toaster } from "@/components/ui/sonner";

const COUNTER_ROLES = ["COUNTER_AGENT", "AGENCY_ADMIN", "SUPER_ADMIN"];
const ADMIN_ROLES = ["SUPER_ADMIN", "AGENCY_ADMIN", "AUDITOR"];

function Home() {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (user) return <Navigate to={landingForRole(user.role)} replace />;
  return <Navigate to="/login" replace />;
}

function App() {
  return (
    <BrowserRouter>
      <I18nProvider>
        <AuthProvider>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/forbidden" element={<AccessDeniedPage />} />
          <Route path="/ticket-status/:ticketId" element={<TicketStatusPage />} />

          {/* Public kiosk + display (no login required, supports DISPLAY_ONLY also) */}
          <Route path="/kiosk" element={<KioskPage />} />
          <Route path="/kiosk/:agencyId" element={<KioskPage />} />
          <Route path="/display" element={<DisplayPage />} />
          <Route path="/display/:agencyId" element={<DisplayPage />} />

          {/* Counter agent */}
          <Route
            path="/counter"
            element={
              <ProtectedRoute roles={COUNTER_ROLES}>
                <CounterPage />
              </ProtectedRoute>
            }
          />

          {/* Admin */}
          <Route
            path="/admin"
            element={
              <ProtectedRoute roles={ADMIN_ROLES}>
                <AdminLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<DashboardPage />} />
            <Route path="agencies" element={<AgenciesPage />} />
            <Route path="counters" element={<CountersPage />} />
            <Route path="categories" element={<CategoriesPage />} />
            <Route path="users" element={<UsersPage />} />
            <Route path="history" element={<HistoryPage />} />
            <Route path="audit" element={<AuditPage />} />
            <Route path="error-logs" element={<ErrorLogsPage />} />
            <Route path="settings" element={<SettingsPage />} />
          </Route>

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        <Toaster position="top-right" richColors />
      </AuthProvider>
      </I18nProvider>
    </BrowserRouter>
  );
}

export default App;
